import {IWedChildrenElt, IWedDisplayElt, IWedletModel, IWedSelector, NoneModel, WED, WedModel} from "/@back@/edit/wed/wedCore.js";
import {WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {BoxModel, WED_SELECTOR_BOX} from "/@back@/edit/wed/wedlets/box/box.js";
import {OtlEditor, OtlTree} from "/@back@/edit/wed/wedlets/outline/outlineTags.js";
import {
	EWedletEditMode,
	IChildWedlet,
	IEditableWedlet,
	IElementWedlet,
	IFindInAreaWedlet,
	IParentWedlet,
	IRootWedlet,
	IS_EltWedlet,
	IVirtualisableWedlet,
	IWedlet,
	IWedletArea,
	IWedletEditModeHint,
	OFindWedletOptions,
	OVisitWedletOptions,
	WEDLET
} from "/@back@/edit/wed/wedlets/wedlet.js";
import {AgWedletSingleElt, EWedletArch, IWedletSingleElt, WEDLET_SINGLEELT} from "/@back@/edit/wed/wedlets/wedletSingleElt.js";
import {DOM, ENodeType, INodeFilter} from "/@lib@/commons/xml/dom.js";
import {IJmlNode, IJmlObj, IJmlSubSet, JmlSubSetIterator} from "/@lib@/commons/xml/jml.js";
import {IXAddr, XA} from "/@lib@/commons/xml/xAddr.js";
import {ISkStructDef} from "/@lib@/edit/schema/schemaAnnots.js";
import {AgWedletInsMgr} from "/@back@/edit/wed/features/insMgr.js";

/**
 *
 * wedlet="OtlNode"
 * Noeud définissant:
 * - otl:entry [obligatoire]: un contenu à afficher pour représenter le noeud (wedlets fils de type Box). otl:entry doit obligatoirement
 *    définir un et seul élément HTML racine (comme Box). Les wedlets fils doivent être de type Box.
 * - otl:hier [optionnel] : des liens vers des noeuds fils de l'outline.
 *    Les wedlets fils peuvent être de type OtlWrapper, OtlNode ou OtlContainer.
 * Le couple otl::entry et otl:hier constitue un "fork", où chacun peut exploiter l'ensemble des fils.
 */
/*
 <wed:bind eltName="dk:section" wedlet="OtlNode" label="Section">
 .<otl:entry>
 ..<box-static>
 ...<wed:children select="dk:headTitle"/>
 ..</box-static>
 .</otl:entry>
 .<otl:hier>
 ..<wed:children select="sp:subSection"/>
 .</otl:hier>
 </wed:bind>
 */

/**
 * wedlet="OtlContainer"
 * Container (invisible du point de vue de l'outline, sans maétrialisation html) d'un ensemble de noeuds.
 * Les wedlets fils peuvent être de tout type :
 * - OtlContainer ou OtlCrossed pour s'enfoncer dans la structure pour trouver le prochain noeud logique du plan,
 * - OtlWrapper ou OtlNode pour constituer la racine du noeud logique fils.
 */
/*
 <wed:bind eltName="sc:item" wedlet="OtlContainer">
 .<wed:children select="dk:section"/>
 </wed:bind>
 */

/**
 * wedlet="OtlWrapper"
 * Enveloppe d'un noeud qui peut contribuer à la construction du contenu du noeud que ce bind enveloppe
 * (surcharge du titre, ajout du type de relation...).
 * Plusieurs wrapper peuvent s'enchainer avant d'atteindre le noeud (si il existe).
 * Exemple d'un OtlWrapper : sm:part d'une compositionPrim.
 * L'intérêt de définir des OtlWrapper (et ne pas directement déclarer un OtlNode) sera pour l'édition "floue"
 * de l'outline : déplacement autorisé en supprimant/remplaçant le wrapper (ie la sm:part).
 * Ce bind doit avoir obligatoirement au moins un élément HTML.
 * Les wedlets fils peuvent être de type Box ou OtlWrapper ou OtlNode (et pas OtlContainer).
 */
/*
 <wed:bind eltName="sp:intro" wedlet="OtlWrapper">
 .<box-static class="intro">
 ..<wed:children/>
 .</box-static>
 </wed:bind>
 */

/**
 * wedlet="OtlCrossed"
 * Bind matérialisé dans le code HTML (contrairement à OtlContainer) mais est simplement
 * traversé, il ne constitue pas un noeud logique dans le plan.
 * A utiliser quand sa matérialisation en HTML est nécessaire pour l'édition : cible de drop...
 * Exemple d'un OtlCrossed : compositionPrim de type set de blocs dont les blocs sont à afficher dans le plan.
 * Ce bind doit définir un élément HTML.
 * Les wedlets fils peuvent être de tout type :
 * - OtlContainer ou OtlCrossed pour s'enfoncer dans la structure pour trouver le prochain noeud logique du plan,
 * - OtlWrapper ou OtlNode pour constituer la racine du noeud logique fils.
 */
/*
 <wed:bind eltName="x:blocks" wedlet="OtlCrossed">
 .<box-static>
 ..<wed:children/>
 .</box-static>
 </wed:bind>
 */

/**
 * Synthèse des types de binds :
 *
 * 									| Matérialisation | Constitue une		| Bind terminal pour
 * 									| 			HTML			| entrée du plan	| une entrée du plan
 * _________________|_________________|_________________|_____________________
 * OtlContainer			|   Non						|			Non					|			Non
 * OtlCrossed				|		Oui						|			Non					|			Non
 * OtlWrapper				|		Oui						|			Oui					|			Non
 * OtlNode					|		Oui						|			Oui					|			Oui
 */

export const OTL_NS = "scenari.eu:wed:outline";


export enum EOtlType {
	container,
	wrapper,
	node,
	crossed
}

export interface IOtlDriver {

	onNewOtlWedlet(wedlet: OtlWedlet): void;

	onDeletedOtlWedlet(wedlet: OtlWedlet): void;

	onOtlRowUpdated(): void;
}

export class OtlModel implements IWedletModel {

	nodeType: ENodeType;
	nodeName: string;
	modes: string[];
	nodeLabel?: string;
	config: Element;
	//entryConfig: Element;
	//containerConfig: Element;

	otlType: EOtlType;

	/** wed:children dans le context otl:entry. */
	entryChildrenElts: IWedChildrenElt[];
	entryChildrenEltsNaturalOrder: IWedChildrenElt[];

	/** wed:children dans le context otl:hier. */
	hierChildrenElts: IWedChildrenElt[];

	/** IWedSelector si entryChildrenElts vide. */
	get defaultWedSelector(): IWedSelector {
		switch (this.otlType) {
		case EOtlType.container:
		case EOtlType.crossed:
			return WED_SELECTOR_OTL;
		case EOtlType.wrapper:
			//dans un wrapper on peut appeler du contenu à afficher ou le sub node/wrapper.
			return WED_SELECTOR_OTL_BOX;
		case EOtlType.node:
			//si pas de wed:children explicite dans un OtlNode, on considère qu'on est dans otl:entry
			return WED_SELECTOR_BOX;
		}
	}

	initModel(cnf: Element) {
		this.config = cnf;
		this.nodeLabel = cnf.getAttribute("label");
		switch (cnf.getAttribute("wedlet")) {
		case "OtlContainer" :
			this.otlType = EOtlType.container;
			this.hierChildrenElts = WEDLET.extractChildrenNodes(cnf, (elt: Element, forChidrenElt?: IWedChildrenElt): IWedSelector => {return WED_SELECTOR_OTL;});
			this.hierChildrenElts.sort(WED.SORT_ChildrenElts);
			break;
		case "OtlNode" :
			this.otlType = EOtlType.node;
			const entryConfig = DOM.findFirstChild(cnf, ((n: Node) => DOM.IS_element(n) && n.localName === 'entry' && n.namespaceURI === OTL_NS) as INodeFilter<Element>);
			const containerConfig = DOM.findFirstChild(cnf, ((n: Node) => DOM.IS_element(n) && n.localName === 'hier' && n.namespaceURI === OTL_NS) as INodeFilter<Element>);
			this.entryChildrenEltsNaturalOrder = WEDLET.extractChildrenNodes(entryConfig, (elt: Element, forChidrenElt?: IWedChildrenElt): IWedSelector => {
				return WEDLET.findWedSelector(elt, forChidrenElt) || WED_SELECTOR_BOX;
			});
			if (this.entryChildrenEltsNaturalOrder) {
				this.entryChildrenElts = this.entryChildrenEltsNaturalOrder.slice();
				this.entryChildrenElts.sort(WED.SORT_ChildrenElts);
			}
			if (containerConfig) {
				this.hierChildrenElts = WEDLET.extractChildrenNodes(containerConfig, (elt: Element, forChidrenElt?: IWedChildrenElt): IWedSelector => {
					return WEDLET.findWedSelector(elt, forChidrenElt) || WED_SELECTOR_OTL;
				});
				this.hierChildrenElts.sort(WED.SORT_ChildrenElts);
			}
			break;
		case "OtlWrapper" :
			this.otlType = EOtlType.wrapper;
			this.entryChildrenEltsNaturalOrder = WEDLET.extractChildrenNodes(cnf, (elt: Element, forChidrenElt?: IWedChildrenElt): IWedSelector => {
				return WEDLET.findWedSelector(elt, forChidrenElt) || WED_SELECTOR_OTL_BOX;
			});
			this.entryChildrenElts = this.entryChildrenEltsNaturalOrder.slice();
			this.entryChildrenElts.sort(WED.SORT_ChildrenElts);
			break;
		case "OtlCrossed" :
			this.otlType = EOtlType.crossed;
			this.hierChildrenElts = WEDLET.extractChildrenNodes(cnf, (elt: Element, forChidrenElt?: IWedChildrenElt): IWedSelector => {return WED_SELECTOR_OTL;});
			this.hierChildrenElts.sort(WED.SORT_ChildrenElts);
			break;
		}
	}

	createWedlet(parent: IParentWedlet, displayContext?: IWedDisplayElt): IChildWedlet {
		switch (this.otlType) {
		case EOtlType.node:
			return new OtlWedletNode().initAsChild(this, parent, displayContext);
		case EOtlType.container:
			return new OtlWedletContainer().initAsChild(this, parent);
		case EOtlType.wrapper:
			return new OtlWedletWrapper().initAsChild(this, parent, displayContext);
		case EOtlType.crossed:
			return new OtlWedletCrossed().initAsChild(this, parent);
		}
	}

	createRootWedlet(wContainer: Element, insertBefore: Node, wedMgr: WedMgr): IRootWedlet {
		switch (this.otlType) {
		case EOtlType.node:
			return new OtlWedletNode().initAsRoot(this, wedMgr);
		case EOtlType.container:
			return new OtlWedletContainer().initAsRoot(this, wedMgr);
		case EOtlType.wrapper:
			return new OtlWedletWrapper().initAsRoot(this, wedMgr);
		case EOtlType.crossed:
			return new OtlWedletCrossed().initAsRoot(this, wedMgr);
		}
	}
}

WED.registerWedletModel("OtlContainer", OtlModel);
WED.registerWedletModel("OtlWrapper", OtlModel);
WED.registerWedletModel("OtlNode", OtlModel);
WED.registerWedletModel("OtlCrossed", OtlModel);

export const WED_SELECTOR_OTL: IWedSelector = function (wedModel: IWedletModel, node: IJmlNode | string, ctx: WedMgr | IWedlet) {
	if (wedModel instanceof OtlModel || wedModel instanceof NoneModel) return WedModel.SELECTOR_PERFECT_MATCH;
	return WedModel.SELECTOR_REJECT;
};
const WED_SELECTOR_OTL_BOX: IWedSelector = function (wedModel: IWedletModel, node: IJmlNode | string, ctx: WedMgr | IWedlet) {
	if (wedModel instanceof OtlModel || wedModel instanceof NoneModel) return WedModel.SELECTOR_PERFECT_MATCH;
	if ((wedModel as BoxModel).isBoxFamily) return 50;
	return WedModel.SELECTOR_REJECT;
};

export interface OFindWedletOtlOptions extends OFindWedletOptions {
	/** Restreint la recherche à la branche de la hiérarchie de l'ouline. Retourne toujours un OtlWedlet. */
	mainBranch?: boolean
}

export interface OVisitWedletOtlOptions extends OVisitWedletOptions {
	/** Restreint la visite à la branche de la hiérarchie de l'ouline. */
	mainBranch?: boolean
}

export abstract class OtlWedlet implements IRootWedlet, IChildWedlet, IParentWedlet, IEditableWedlet {

	model: OtlModel;
	wedMgr: WedMgr;
	xaPart: number;
	wedParent: IParentWedlet;
	editMode: EWedletEditMode;

	get wedAnchor(): IXAddr {return this.wedParent ? XA.append(this.wedParent.wedAnchor, this.xaPart) : this._rootXa}

	isVirtual(): this is IVirtualisableWedlet {return false}

	abstract otlType: EOtlType;

	/** OtlTree auquel ce wrapper/node racine est connecté. Seuls les wrapper et node **racines** sont connectés. */
	otlTree: OtlTree;

	/** Traverse les wedlets Box. */
	get otlParent(): OtlWedlet {return this.wedParent instanceof OtlWedlet ? this.wedParent : null}

	get otlDriver(): IOtlDriver {return this._otlDriver || this.otlParent ? this.otlParent.otlDriver : this.wedMgr.wedEditor as any as IOtlDriver}

	protected _otlDriver: IOtlDriver;

	/** pour le wedlet IRootWedlet. */
	protected _rootXa: IXAddr;

	initAsRoot(model: OtlModel, wedMgr: WedMgr): this {
		this.model = model;
		this.wedMgr = wedMgr;
		this.createElement();
		return this;
	}

	initAsChild(model: OtlModel, parent: IParentWedlet): this {
		this.model = model;
		this.wedParent = parent as OtlWedlet;
		this.wedMgr = parent.wedMgr;
		this.createElement();
		return this;
	}

	bindRoot(jmlChildren: IJmlSubSet, jmlRoot?: IJmlNode, xaRoot?: IXAddr): void | Promise<void> {
		this._rootXa = XA.freeze(xaRoot || []);
		this.otlDriver.onNewOtlWedlet(this);
		return this.endBind(this._appendChildWedlets(jmlChildren));
	}

	isEmpty() {return false}

	protected endBind(promises: Promise<any>[]): void | Promise<void> {
		if (promises) return Promise.all(promises).then(() => {this.otlDriver.onOtlRowUpdated()});
		this.refreshEditMode();
		this.otlDriver.onOtlRowUpdated();
	}

	abstract bindWithNode(xaOffset: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void>;

	/** Navigue en éliminant les containers. */
	abstract findPreviousSiblingHier(fromChild: OtlWedlet): OtlWedletWrapper;

	abstract findNextSiblingHier(fromChild: OtlWedlet): OtlWedletWrapper;

	/** Navigue en éliminant les containers. */
	abstract findFirstHier(): OtlWedletWrapper;

	/** Navigue en éliminant les containers. */
	abstract findLastHier(): OtlWedletWrapper;

	protected abstract createElement(): void;

	protected _appendChildWedlets(children: IJmlSubSet, promises?: Promise<void>[]): Promise<void>[] {
		const it = new JmlSubSetIterator(children);
		while (it.next()) {
			const promise = this._insertChildWedlet(it.currentIdx, it.currentNode, it.currentChildren);
			if (promise) {
				if (!promises) promises = [promise];
				else promises.push(promise);
			}
		}
		return promises;
	}

	protected abstract _insertChildWedlet(xaOffset: number, node: IJmlNode | number, children: IJmlSubSet, insertBefore?: Element): void | Promise<void>;

	refreshEditMode(): void {
		this.editMode = WEDLET.resolveEditMode(this);
	}

	getEditModeForDesc(target: IWedlet): IWedletEditModeHint | undefined {
		//On force le mode readStatic pour les wedlets Box constituant le contenu.
		return target instanceof OtlWedlet ? undefined : WEDLET.READSTATIC_MUST;
	}
}

export class OtlWedletWrapper extends OtlWedlet implements IParentWedlet, IWedletSingleElt, IVirtualisableWedlet, IFindInAreaWedlet {

	element: IElementWedlet;

	get elementHost(): HTMLElement {return this.element};

	get otlType(): EOtlType {return EOtlType.wrapper};

	/** Pas de restriction par intersection pour un OtlWedletWrapper. */
	get wedletRect(): DOMRectReadOnly | null {return null}

	initAsChild(model: OtlModel, parent: IParentWedlet, displayContext?: IWedDisplayElt): this {
		super.initAsChild(model, parent);
		this.displayCtx = displayContext;
		return this;
	}

	protected createElement() {
		if (this.element) throw Error("Element already setted.");
		const root = this.model.config.firstElementChild;
		this.element = document.importNode(root, false) as IElementWedlet;
		this.element.configWedletElt(root, this);
	}

	insertElement(parent: Element, insertBefore?: Node, slotName?: string, caller?: IWedChildrenElt) {
		if (slotName) this.element.setAttribute("slot", slotName);
		if (caller) this.element.fromChildrenElt = caller;
		parent.insertBefore(this.element, insertBefore);
	}


	bindWithNode(xaOffset: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		this.xaPart = xaOffset;
		this.element.refreshBindValue(node, children);
		this.otlDriver.onNewOtlWedlet(this);
		let promises = WEDLET.appendAttWedlets(this, node, this._insertAttrNode);
		if (children) promises = this._appendChildWedlets(children, promises);
		WEDLET_SINGLEELT.adjustVirtuals(this, this.model.entryChildrenEltsNaturalOrder);
		return this.endBind(promises);
	}

	bindRoot(jmlChildren: IJmlSubSet, jmlRoot?: IJmlNode, xaRoot?: IXAddr): void | Promise<void> {
		const pr = super.bindRoot(jmlChildren, jmlRoot, xaRoot);
		WEDLET_SINGLEELT.adjustVirtuals(this, this.model.entryChildrenEltsNaturalOrder);
		return pr;
	}

	deleteChildNodes(xaOffest: number, count: number) {
		this.visitWedletChildren(xaOffest, count, (wedlet: IChildWedlet): 'stop' | void => {
			if (wedlet instanceof OtlWedlet) this.otlDriver.onDeletedOtlWedlet(wedlet);
		});
		WEDLET_SINGLEELT.deleteChildNodes(this, xaOffest, count);
		WEDLET_SINGLEELT.adjustVirtuals(this, this.model.entryChildrenEltsNaturalOrder);
	}

	insertAttrNode(attrName: string, value: string): void | Promise<void> {
		const result = this._insertAttrNode(attrName, value);
		WEDLET_SINGLEELT.adjustVirtuals(this, this.model.entryChildrenEltsNaturalOrder);
		return result;
	}

	protected _insertAttrNode(attrName: string, value: string): void | Promise<void> {
		return WEDLET_SINGLEELT.insertAttrNode(this, attrName, value, this.model.entryChildrenElts, this.model.defaultWedSelector);
	}

	deleteAttrNode(attName: string) {
		const attWedlet = this.findWedletChild(attName);
		if (attWedlet instanceof OtlWedlet) this.otlDriver.onDeletedOtlWedlet(attWedlet);
		WEDLET_SINGLEELT.deleteAttrNode(this, attName);
		WEDLET_SINGLEELT.adjustVirtuals(this, this.model.entryChildrenEltsNaturalOrder);
	}

	findPreviousSiblingHier(fromChild: OtlWedlet): OtlWedletWrapper {
		return this.otlParent ? this.otlParent.findPreviousSiblingHier(this) : null;
	}

	findNextSiblingHier(fromChild: OtlWedlet): OtlWedletWrapper {
		return this.otlParent ? this.otlParent.findNextSiblingHier(this) : null;
	}

	findFirstHier(): OtlWedletWrapper {return this}

	findLastHier(): OtlWedletWrapper {return this}


	insertChildNode(xaOffest: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		let result;
		//on cherche le point d'insertion et on décale les autres binds
		for (let ch = this.elementHost.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (IS_EltWedlet(ch)) {
				const xaPart = ch.wedlet.xaPart as number;
				if (xaPart > xaOffest) {
					ch.wedlet.xaPart = (xaPart as number) + 1;
				} else if (xaPart === xaOffest) {
					result = this._insertChildWedlet(xaOffest, node, children, ch) || null;
					ch.wedlet.xaPart = (xaPart as number) + 1;
				}
			}
		}
		//si pas trouvé on insère à la fin.
		if (result === undefined) result = this._insertChildWedlet(xaOffest, node, children, null);
		return result;
	}

	onChildNodesInserted() {
		WEDLET_SINGLEELT.adjustVirtuals(this, this.model.entryChildrenEltsNaturalOrder);
	}

	replaceChildBind(xaOffest: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		for (let ch = this.elementHost.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (IS_EltWedlet(ch)) {
				if (ch.wedlet.xaPart === xaOffest) {
					let next = ch.nextElementSibling;
					while (IS_EltWedlet(next) && next.wedlet.isVirtual()) next = next.nextElementSibling;
					if ('onDelete' in ch.wedlet) ch.wedlet.onDelete();
					ch.remove(); //supprimé avant sinon le display virtuel correspondant n'est pas remplacé car offset incorrect.
					const result = this._insertChildWedlet(xaOffest, node, children, next);
					WEDLET_SINGLEELT.adjustVirtuals(this, this.model.entryChildrenEltsNaturalOrder);
					return result;
				}
			}
		}
	}

	injectForeignChild(similarTo: IJmlObj | ISkStructDef | Node | Attr, elt: Element, before?: IWedlet, after?: IWedlet, options?: Dict<any>) {
		//console.trace("injectForeignChild:::", this.element, before, after);
		if (before) {
			const otlTree = OtlEditor.findOtlTree(before);
			if (otlTree) otlTree.parentNode.insertBefore(elt, otlTree);
		} else if (after) {
			const otlTree = OtlEditor.findOtlTree(after);
			if (otlTree) otlTree.parentNode.insertBefore(elt, otlTree.nextSibling);
		} else {
			const otlTree = OtlEditor.findOtlTree(this);
			if (otlTree) otlTree.otlChildrenCtn.appendChild(elt);
		}
	}

	protected _insertChildWedlet(xaOffset: number, node: IJmlNode | number, children: IJmlSubSet, insertBefore?: Element): void | Promise<void> {
		if (typeof node === 'number') return;
		return WEDLET_SINGLEELT.insertChildNode(this, xaOffset, node, children, this.model.entryChildrenElts, this.model.defaultWedSelector, insertBefore);
	}


	/** Contexte de l'affichage forcé de ce wedlet. */
	displayCtx: IWedDisplayElt;

	isVirtual(): this is IVirtualisableWedlet {return this.xaPart == null}

	/**
	 * Par opposition à IChildWedlet.bindWithNode() et ICharsWedlet.bindWithAttr(),
	 * associe ce wedlet à un noeud virtual.
	 */
	bindAsVirtual(): void {
		this.xaPart = null;
		this.element.refreshBindValue(null);
		this.otlDriver.onNewOtlWedlet(this);
		WEDLET_SINGLEELT.adjustVirtuals(this, this.model.entryChildrenEltsNaturalOrder);
		this.endBind(null);
	}

	deleteVirtualWedlet(): void {this.element.remove()}

	/**
	 * Crée un nouveau noeud en vue de concrétiser ce wedlet virtual.
	 * Ne peut être utilisé sur un attribut ou un document.
	 * Permet en particulier sur un élément d'ajouter des attributs (xml:space, role...)
	 */
	newJml(): IJmlNode {throw Error("not implemented")}

	doCustomAdjust() {
		if (this.element.onChildWedletsChange) this.element.onChildWedletsChange();
	}
}

export interface OtlWedletWrapper extends IParentWedlet {
	model: OtlModel;
	xaPart: number;

	findWedletChild(xaPart: string | number, options?: OFindWedletOtlOptions): IChildWedlet | IChildWedlet[];

	visitWedletChildren?(from: number, len: number, visitor: (this: null, wedlet: IChildWedlet) => 'stop' | void, options?: OVisitWedletOtlOptions): 'stop' | void;

	// Ajouté par AgWedletSingleElt.
	findInArea(cb: (wedletArea: IWedletArea) => 'next' | 'nextSibling' | 'stop', area: DOMRectReadOnly, visitOptions?: OVisitWedletOptions): void | 'stop'
}


AgWedletSingleElt(OtlWedletWrapper, {wedletArch: EWedletArch.dom, isParentWedlet: true, isVirtualisableWedlet: true /*, proxySkAnnots: true*/});
AgWedletInsMgr(OtlWedletWrapper);


export class OtlWedletNode extends OtlWedletWrapper {

	element: IElementWedlet;

	get elementHost(): HTMLElement {return this.element};

	get otlType(): EOtlType {return EOtlType.node};

	hierWedlets: OtlWedlet[] | null;

	get wedletRect(): DOMRectReadOnly | null {return OtlEditor.findOtlTree(this)?.getBoundingClientRect()}

	protected createElement() {
		if (this.element) throw Error("Element already setted.");
		const root = this.model.config.querySelector("entry").firstElementChild;
		this.element = document.importNode(root, false) as IElementWedlet;
		this.element.configWedletElt(root, this);
	}

	findWedletChild(xaPart: string | number, options?: OFindWedletOtlOptions): IChildWedlet | IChildWedlet[] {
		const entryCh = !options || !options.mainBranch ? super.findWedletChild(xaPart, options) as IChildWedlet : null;
		const hierCh = this.hierWedlets ? this.hierWedlets.find((e: IChildWedlet) => e.xaPart === xaPart) : null;
		return entryCh ? (hierCh ? [entryCh, hierCh] : entryCh) : hierCh;
	}

	visitWedletChildren(from: number, len: number, visitor: (this: null, wedlet: IChildWedlet) => 'stop' | void, options?: OVisitWedletOtlOptions): 'stop' | void {
		if (!options || !options.mainBranch) {
			if (super.visitWedletChildren(from, len, visitor) === 'stop') return 'stop';
		}
		const end = from + len;
		if (this.hierWedlets) for (let i = 0; i < this.hierWedlets.length; i++) {
			const e = this.hierWedlets[i];
			const xaPart = e.xaPart;
			if (xaPart != null && (typeof xaPart === 'number' ? xaPart >= from : from === -1)) {
				if (xaPart >= end) return;
				if (visitor.call(null, e) === 'stop') return 'stop';
			}
		}
	}

	findPreviousSiblingHier(child: OtlWedlet): OtlWedletWrapper {
		if (this.hierWedlets) for (let i = 0; i < this.hierWedlets.length; i++) {
			if (this.hierWedlets[i] === child) return i > 0 ? this.hierWedlets[i - 1].findLastHier() : null;
		}
		return null;
	}

	findNextSiblingHier(child: OtlWedlet): OtlWedletWrapper {
		if (this.hierWedlets) for (let i = 0; i < this.hierWedlets.length; i++) {
			if (this.hierWedlets[i] === child) return i < this.hierWedlets.length - 1 ? this.hierWedlets[i + 1].findFirstHier() : null;
		}
		return null;
	}

	deleteChildNodes(xaOffest: number, count: number) {
		const xaEnd = xaOffest + count;
		if (this.hierWedlets) {
			let found = false;
			for (let i = 0; i < this.hierWedlets.length; i++) {
				const ch = this.hierWedlets[i];
				const xaPart = ch.xaPart;
				if (xaPart >= xaEnd) {
					ch.xaPart -= count;
				} else if (xaPart >= xaOffest) {
					this.otlDriver.onDeletedOtlWedlet(ch);
					this.hierWedlets.splice(i--, 1);
					found = true;
				}
			}
			if (found) this.otlDriver.onOtlRowUpdated();
		}
		WEDLET_SINGLEELT.deleteChildNodes(this, xaOffest, count);
		WEDLET_SINGLEELT.adjustVirtuals(this, this.model.entryChildrenEltsNaturalOrder);
	}

	protected _insertChildWedlet(xaOffset: number, node: IJmlNode | number, children: IJmlSubSet, insertBefore ?: Element): void | Promise<void> {
		const promises = super._insertChildWedlet(xaOffset, node, children, insertBefore);
		if (typeof node === 'number') return promises;
		let insertOffset = this.hierWedlets ? this.hierWedlets.length : 0;
		while (insertOffset > 0 && this.hierWedlets[insertOffset - 1].xaPart >= xaOffset) {
			const ch = this.hierWedlets[insertOffset - 1];
			ch.xaPart = (ch.xaPart as number) + 1;
			insertOffset--;
		}
		const hierChildrenElt = WEDLET.findChildrenEltForNodeStrict(this.model.hierChildrenElts, node);
		if (hierChildrenElt) {
			const model = this.wedMgr.wedModel.findModelForNodeFromCh(node, this, hierChildrenElt);
			const wedletCh = model ? model.createWedlet(this) as OtlWedlet : null;
			if (!wedletCh) return;
			if (!this.hierWedlets) this.hierWedlets = [];
			this.hierWedlets.splice(insertOffset, 0, wedletCh);
			return wedletCh.bindWithNode(xaOffset, node, children);
		}
		return promises;
	}

}

export class OtlWedletContainer extends OtlWedlet implements IParentWedlet {

	hierWedlets: OtlWedlet[] = [];

	// @ts-ignore
	get otlType(): EOtlType {return EOtlType.container};

	bindWithNode(xaOffset: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		this.xaPart = xaOffset;
		this.otlDriver.onNewOtlWedlet(this);
		return this.endBind(children ? this._appendChildWedlets(children) : null);
	}

	findWedletChild(xaPart: string | number, options?: any): IChildWedlet | IChildWedlet[] {
		return this.hierWedlets.find((e: IChildWedlet) => e.xaPart === xaPart);
	}

	visitWedletChildren(from: number, len: number, visitor: (this: null, wedlet: IChildWedlet) => 'stop' | void, options?: OVisitWedletOptions): 'stop' | void {
		const end = from + len;
		for (let i = 0; i < this.hierWedlets.length; i++) {
			const e = this.hierWedlets[i];
			const xaPart = e.xaPart;
			if (xaPart != null && (typeof xaPart === 'number' ? xaPart >= from : from === -1)) {
				if (xaPart >= end) return;
				if (visitor.call(null, e) === 'stop') return 'stop';
			}
		}
	}

	findPreviousSiblingHier(fromChild: OtlWedlet): OtlWedletWrapper {
		for (let i = 0; i < this.hierWedlets.length; i++) {
			if (this.hierWedlets[i] === fromChild) {
				if (i === 0) return this.otlParent ? this.otlParent.findPreviousSiblingHier(this) : null;
				return this.hierWedlets[i - 1].findLastHier();
			}
		}
		throw Error("Child not found: " + fromChild);
	}

	findNextSiblingHier(fromChild: OtlWedlet): OtlWedletWrapper {
		for (let i = 0, s = this.hierWedlets.length - 1; i <= s; i++) {
			if (this.hierWedlets[i] === fromChild) {
				if (i === s) return this.otlParent ? this.otlParent.findNextSiblingHier(this) : null;
				return this.hierWedlets[i + 1].findFirstHier();
			}
		}
		throw Error("Child not found: " + fromChild);
	}


	findFirstHier(): OtlWedletWrapper {return this.hierWedlets && this.hierWedlets[0] ? this.hierWedlets[0].findFirstHier() : null}

	findLastHier(): OtlWedletWrapper {return this.hierWedlets && this.hierWedlets.length > 0 ? this.hierWedlets[this.hierWedlets.length - 1].findLastHier() : null}


	protected createElement() {
		//pas d'element pour un container.
	}

	insertChildNode(xaOffest: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		return this._insertChildWedlet(xaOffest, node, children);
	}

	onChildNodesInserted() {}

	deleteChildNodes(xaOffest: number, count: number) {
		const xaEnd = xaOffest + count;
		for (let i = 0; i < this.hierWedlets.length; i++) {
			const ch = this.hierWedlets[i];
			const xaPart = ch.xaPart;
			if (xaPart >= xaEnd) {
				ch.xaPart -= count;
			} else if (xaPart >= xaOffest) {
				this.otlDriver.onDeletedOtlWedlet(ch);
				this.hierWedlets.splice(i--, 1);
			}
		}
		this.otlDriver.onOtlRowUpdated();
	}

	protected _insertChildWedlet(xaOffset: number, node: IJmlNode | number, children: IJmlSubSet): void | Promise<void> {
		if (typeof node === 'number') return;
		const hierChildrenElt = WEDLET.findChildrenEltForNodeStrict(this.model.hierChildrenElts, node);
		if (hierChildrenElt) {
			const wedMgr = this.wedMgr;
			let wedletCh: OtlWedlet;
			const model = wedMgr.wedModel.findModelForNodeFromCh(node, this, hierChildrenElt);
			wedletCh = model ? model.createWedlet(this) as OtlWedlet : null;
			if (!wedletCh) return;
			let insertOffset = this.hierWedlets.length;
			while (insertOffset > 0 && this.hierWedlets[insertOffset - 1].xaPart >= xaOffset) {
				const ch = this.hierWedlets[insertOffset - 1];
				ch.xaPart = (ch.xaPart as number) + 1;
				insertOffset--;
			}
			this.hierWedlets.splice(insertOffset, 0, wedletCh);
			return wedletCh.bindWithNode(xaOffset, node, children);
		}
	}
}


export class OtlWedletCrossed extends OtlWedletContainer implements IWedletSingleElt, IFindInAreaWedlet {

	get otlType(): EOtlType {return EOtlType.crossed};

	element: IElementWedlet;

	get elementHost(): HTMLElement {return this.element};

	/** Pas de restriction par intersection pour un OtlWedletWrapper. */
	get wedletRect(): DOMRectReadOnly | null {return null}

	protected createElement() {
		if (this.element) throw Error("Element already setted.");
		const root = this.model.config.firstElementChild;
		this.element = document.importNode(root, false) as IElementWedlet;
		this.element.configWedletElt(root, this);
	}

	// insertElement(parent: Element, insertBefore?: Node, slotName?: string, caller?: IWedChildrenElt) {
	// 	if (slotName) this.element.setAttribute("slot", slotName);
	// 	if (caller) this.element.fromChildrenElt = caller;
	// 	parent.insertBefore(this.element, insertBefore);
	// }


	injectForeignChild(similarTo: IJmlObj | ISkStructDef | Node | Attr, elt: Element, before?: IWedlet, after?: IWedlet, options?: Dict<any>) {
		//console.trace("injectForeignChild:::", this.element, before, after);
		if (before) {
			const otlTree = OtlEditor.findOtlTree(before);
			if (otlTree) otlTree.parentNode.insertBefore(elt, otlTree);
		} else if (after) {
			const otlTree = OtlEditor.findOtlTree(after);
			if (otlTree) otlTree.parentNode.insertBefore(elt, otlTree.nextSibling);
		} else {
			this.elementHost.appendChild(elt);
		}
	}
}

export interface OtlWedletCrossed {
	// Ajouté par AgWedletSingleElt.
	findInArea(cb: (wedletArea: IWedletArea) => 'next' | 'nextSibling' | 'stop', area: DOMRectReadOnly, visitOptions?: OVisitWedletOptions): void | 'stop'
}

AgWedletSingleElt(OtlWedletCrossed, {wedletArch: EWedletArch.custom, isParentWedlet: true});
AgWedletInsMgr(OtlWedletCrossed)
